// src/screens/LoginScreen/LoginScreen.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './login-screen.css';
import { signInWithGoogle, signInWithApple } from '../../utils/firebase';
import { useAuth } from '../../context/AuthContext';
import { AppLoadingScreen } from '../../components/common/AppLoadingScreen/AppLoadingScreen';

const LoginScreen = () => {
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  // Once auth resolves to a logged-in user, warm up the dashboard's data
  // (same request DashboardScreen itself makes) while showing our own
  // "Loading your dashboard…" state below, then navigate — so the user
  // never sees a separate blank/loading dashboard screen.
  useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;

    // Warm the dashboard's own stats request for this specific account (never
    // a shared placeholder id — that would prefetch/pollute another user's data).
    const warmDashboardData = fetch(`/api/user-stats?user_id=${user.uid}`).catch(() => {});
    const maxWait = new Promise((resolve) => setTimeout(resolve, 4000));

    Promise.race([warmDashboardData, maxWait]).finally(() => {
      if (!cancelled) navigate('/dashboard', { replace: true });
    });

    return () => {
      cancelled = true;
    };
  }, [user, navigate]);

  const handleGoogle = async () => {
    setError(null);
    setLoading(true);
    try {
      await signInWithGoogle();
    } catch (err) {
      console.error('Google sign-in error:', err);
      if (err.code !== 'auth/popup-closed-by-user' && err.code !== 'auth/cancelled-popup-request') {
        setError('Sign-in failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleApple = async () => {
    setError(null);
    setLoading(true);
    try {
      await signInWithApple();
    } catch (err) {
      console.error('Apple sign-in error:', err);
      if (err.code !== 'auth/popup-closed-by-user' && err.code !== 'auth/cancelled-popup-request') {
        setError('Sign-in failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Graphic/title shell is AppLoadingScreen (shared with App.jsx's boot
  // gate and ProtectedRoute's auth check, and matching the native launch
  // screen's artwork) so this screen is a seamless continuation of
  // whatever loading state preceded it, not a new screen. Sign-in buttons
  // render in AppLoadingScreen's status slot; once auth resolves, that
  // slot's status text ("Loading your dashboard…") takes over automatically.
  return (
    <AppLoadingScreen status={user ? 'Loading your dashboard…' : null}>
      {!user ? (
        <div className="login-signin-section">
          <span className="login-signin-label">Sign in with</span>
          <div className="login-auth-buttons">
            <button
              className={`login-btn-apple${loading ? ' login-btn-loading' : ''}`}
              onClick={handleApple}
              aria-label="Sign in with Apple"
            >
              <img src="/svgs/icon-apple.svg" alt="Apple" />
            </button>
            <button
              className={`login-btn-google${loading ? ' login-btn-loading' : ''}`}
              onClick={handleGoogle}
              aria-label="Sign in with Google"
            >
              <img src="/svgs/icon-google.svg" alt="Google" />
            </button>
          </div>
          {error && <p className="login-error">{error}</p>}
        </div>
      ) : null}
    </AppLoadingScreen>
  );
};

export default LoginScreen;
